import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, CircularProgress, Alert, Breadcrumbs, Link, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowDown, faArrowUp, faChevronRight, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '8px 12px', fontSize: 10, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px',
        textAlign: align, backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted, color }) => (
    <td style={{
        padding: '8px 12px', fontSize: 12.5,
        color: color || (muted ? '#9ca3af' : '#374151'),
        fontWeight: muted ? 400 : 500, textAlign: align,
        borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const fmtKg = (n) => (n != null ? `${fmt(n)} kg` : '—');

const formatDate = (d) => {
    if (!d) return '—';
    const clean = String(d).split('T')[0];
    try {
        return new Date(clean).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return clean;
    }
};

const TotalPill = ({ label, value, sub, color }) => (
    <div style={{ flex: '1 1 140px', minWidth: 130, border: '1px solid #f3f4f6', borderRadius: 12, padding: '12px 14px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: color || '#111827', marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
);

const EmptyState = ({ message }) => (
    <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12.5, border: '1px dashed #e5e7eb', borderRadius: 10 }}>
        {message}
    </div>
);

const SpinnerBox = () => (
    <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress size={36} thickness={4} sx={{ color: '#189AB4' }} />
    </Box>
);

/* ---- Level 1: Sub Category → Product (size) split ---- */
const SizeSplitView = ({ data, onProductClick }) => {
    const { t } = useTranslation();
    const totals = data?.totals ?? {};
    const products = data?.products ?? [];
    return (
        <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <TotalPill label={t('movementAnalytics.labels.purchased')} color="#059669" value={fmtKg(totals.purchase_weight_kg)} />
                <TotalPill label={t('movementAnalytics.labels.sold')} color="#ef4444" value={fmtKg(totals.sales_weight_kg)} />
                <TotalPill
                    label={(totals.net_weight_kg ?? 0) >= 0 ? t('movementAnalytics.labels.netInflow') : t('movementAnalytics.labels.netOutflow')}
                    color={(totals.net_weight_kg ?? 0) >= 0 ? '#059669' : '#d97706'}
                    value={fmtKg(Math.abs(totals.net_weight_kg ?? 0))}
                    sub={`${fmt(totals.products_moved)} ${t('movementAnalytics.labels.productsSuffix')}`}
                />
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                {t('movementAnalytics.headings.productsSizesClickToSeeOrders')}
            </div>
            {!products.length ? (
                <EmptyState message={t('movementAnalytics.emptyStates.noProductsMoved')} />
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #f3f4f6', borderRadius: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                        <thead>
                            <tr>
                                <Th>{t('movementAnalytics.columns.product')}</Th>
                                <Th>{t('movementAnalytics.columns.size')}</Th>
                                <Th align="right">{t('movementAnalytics.columns.purchase')}</Th>
                                <Th align="right">{t('movementAnalytics.columns.sales')}</Th>
                                <Th align="right">{t('movementAnalytics.columns.net')}</Th>
                                <Th align="center" />
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p, i) => {
                                const net = p.net_weight_kg ?? 0;
                                return (
                                    <tr
                                        key={p.product_id ?? i}
                                        onClick={() => onProductClick?.(p)}
                                        style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#fff' : '#fafafa')}
                                    >
                                        <Cell><span style={{ fontWeight: 700, color: '#111827' }}>{p.alias_name ?? `#${p.product_id}`}</span></Cell>
                                        <Cell muted>{p.product_size}</Cell>
                                        <Cell align="right" color="#059669">{fmtKg(p.purchase?.weight_kg)}</Cell>
                                        <Cell align="right" color="#ef4444">{fmtKg(p.sales?.weight_kg)}</Cell>
                                        <Cell align="right" color={net >= 0 ? '#059669' : '#d97706'}>
                                            <span style={{ fontWeight: 700 }}>{net >= 0 ? '+' : ''}{fmt(net)} kg</span>
                                        </Cell>
                                        <Cell align="center">
                                            <FontAwesomeIcon icon={faChevronRight} style={{ color: '#cbd5e1', fontSize: 12 }} />
                                        </Cell>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

/* ---- Level 2: Product (size) → Orders / POs ---- */
const OrdersTable = ({ title, icon, color, rows = [], numberKey, numberLabel, dateKey }) => {
    const { t } = useTranslation();
    return (
    <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <FontAwesomeIcon icon={icon} style={{ color }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{title}</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>({rows.length})</span>
        </div>
        {!rows.length ? (
            <EmptyState message={t('movementAnalytics.emptyStates.noRecordsInPeriod')} />
        ) : (
            <div style={{ overflowX: 'auto', border: '1px solid #f3f4f6', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                    <thead>
                        <tr>
                            <Th>{numberLabel}</Th>
                            <Th>{t('movementAnalytics.columns.date')}</Th>
                            <Th align="right">{t('movementAnalytics.columns.challans')}</Th>
                            <Th align="right">{t('movementAnalytics.columns.weight')}</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={(r[numberKey] ?? i)} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                <Cell><span style={{ fontWeight: 700, color }}>{r[numberLabel === t('movementAnalytics.columns.poNo') ? 'po_number' : 'order_number'] ?? '—'}</span></Cell>
                                <Cell muted>{formatDate(r[dateKey])}</Cell>
                                <Cell align="right" muted>{fmt(r.challan_count)}</Cell>
                                <Cell align="right">{fmtKg(r.weight_kg)}</Cell>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
    );
};

const ProductOrdersView = ({ data }) => {
    const { t } = useTranslation();
    const totals = data?.totals ?? {};
    return (
        <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <TotalPill label={t('movementAnalytics.labels.purchased')} color="#059669" value={fmtKg(totals.purchase_weight_kg)} sub={`${fmt(totals.purchase_order_count)} ${t('movementAnalytics.labels.posSuffix')}`} />
                <TotalPill label={t('movementAnalytics.labels.sold')} color="#ef4444" value={fmtKg(totals.sales_weight_kg)} sub={`${fmt(totals.sales_order_count)} ${t('movementAnalytics.labels.ordersSuffix')}`} />
                <TotalPill
                    label={(totals.net_weight_kg ?? 0) >= 0 ? t('movementAnalytics.labels.netInflow') : t('movementAnalytics.labels.netOutflow')}
                    color={(totals.net_weight_kg ?? 0) >= 0 ? '#059669' : '#d97706'}
                    value={fmtKg(Math.abs(totals.net_weight_kg ?? 0))}
                />
            </div>
            <OrdersTable
                title={t('movementAnalytics.sections.salesOrders')} icon={faArrowUp} color="#ef4444"
                rows={data?.sales_orders} numberKey="order_id" numberLabel={t('movementAnalytics.columns.orderNo')} dateKey="order_date"
            />
            <OrdersTable
                title={t('movementAnalytics.sections.purchaseOrders')} icon={faArrowDown} color="#059669"
                rows={data?.purchase_orders} numberKey="po_id" numberLabel={t('movementAnalytics.columns.poNo')} dateKey="po_date"
            />
        </>
    );
};

const MovementDrilldownModal = ({
    open, onClose,
    subCategoryName,
    level,                 // 'sizes' | 'orders'
    sizeSplit, sizeSplitLoading, sizeSplitError,
    productOrders, productOrdersLoading, productOrdersError,
    activeProductLabel,
    onProductClick,
    onBack,
    onExport,
    exporting = false,
    exportError = '',
}) => {
    const { t } = useTranslation();
    const atOrders = level === 'orders';
    const canExport = !atOrders && !sizeSplitLoading && !sizeSplitError && !!sizeSplit && typeof onExport === 'function';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Breadcrumbs separator="›" sx={{ fontSize: 13 }}>
                        <Link
                            component="button"
                            underline={atOrders ? 'hover' : 'none'}
                            onClick={atOrders ? onBack : undefined}
                            sx={{ fontSize: 16, fontWeight: 800, color: atOrders ? '#189AB4' : '#111827', cursor: atOrders ? 'pointer' : 'default' }}
                        >
                            {sizeSplit?.sub_category_name ?? subCategoryName ?? t('movementAnalytics.labels.subCategoryFallback')}
                        </Link>
                        {atOrders && (
                            <span style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                                {productOrders?.product_alias_name ?? activeProductLabel ?? t('movementAnalytics.labels.productFallback')}
                                {productOrders?.product_size ? ` · ${productOrders.product_size}` : ''}
                            </span>
                        )}
                    </Breadcrumbs>
                    <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                        {atOrders ? t('movementAnalytics.subtitles.parentOrders') : t('movementAnalytics.subtitles.productSizeSplit')}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canExport && (
                        <Button
                            onClick={onExport}
                            disabled={exporting}
                            size="small"
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={exporting ? faSpinner : faDownload} spin={exporting} />}
                            sx={{
                                textTransform: 'none', fontWeight: 700, borderRadius: 2,
                                color: '#189AB4', borderColor: '#189AB4',
                                '&:hover': { borderColor: '#147a8f', backgroundColor: 'rgba(24,154,180,0.06)' },
                            }}
                        >
                            {exporting ? t('movementAnalytics.labels.exporting') : t('movementAnalytics.buttons.exportCsv')}
                        </Button>
                    )}
                    <IconButton onClick={onClose} size="small"><FontAwesomeIcon icon={faTimes} /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {!atOrders && (
                    <>
                        {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
                        {sizeSplitLoading && <SpinnerBox />}
                        {!sizeSplitLoading && sizeSplitError && (
                            <Alert severity="error">{typeof sizeSplitError === 'object' ? JSON.stringify(sizeSplitError) : sizeSplitError}</Alert>
                        )}
                        {!sizeSplitLoading && !sizeSplitError && sizeSplit && (
                            <SizeSplitView data={sizeSplit} onProductClick={onProductClick} />
                        )}
                    </>
                )}
                {atOrders && (
                    <>
                        {productOrdersLoading && <SpinnerBox />}
                        {!productOrdersLoading && productOrdersError && (
                            <Alert severity="error">{typeof productOrdersError === 'object' ? JSON.stringify(productOrdersError) : productOrdersError}</Alert>
                        )}
                        {!productOrdersLoading && !productOrdersError && productOrders && (
                            <ProductOrdersView data={productOrders} />
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MovementDrilldownModal;
